/**
 * ElevationProfileChart — Chart.js line chart for elevation profiles.
 *
 * Renders an elevation-vs-distance chart from sampled profile data.
 * Uses Chart.js via dynamic import to avoid bundling it when the
 * terrain module is disabled.
 */

import { useEffect, useRef, useState } from 'react';
import { tokens } from '@/design';
import type { ElevationProfileSample } from '@/sdk-stub/gtwin-sdk/terrain-types';
import { profileStats } from './elevationProfileSampler';

export interface ElevationProfileChartProps {
  samples: readonly ElevationProfileSample[];
  height?: number;
  'data-testid'?: string;
}

function formatDistance(metres: number): string {
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres.toFixed(0)} m`;
}

export function ElevationProfileChart({
  samples,
  height = 200,
  'data-testid': testId,
}: ElevationProfileChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<unknown>(null);
  const [_chartReady, setChartReady] = useState(false);

  const stats = profileStats([...samples]);

  useEffect(() => {
    if (!canvasRef.current || samples.length === 0) return;
    let cancelled = false;

    (async () => {
      try {
        const { Chart, registerables } = await import('chart.js');
        if (cancelled) return;
        Chart.register(...registerables);

        // Destroy previous chart instance
        if (chartRef.current) {
          (chartRef.current as any).destroy();
        }

        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;

        const labels = samples.map((s) => formatDistance(s.distance));
        const data = samples.map((s) => s.elevation);

        chartRef.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'Elevation (m)',
                data,
                borderColor: tokens.color.accent.primary,
                backgroundColor: `${tokens.color.accent.primary}33`,
                fill: true,
                pointRadius: 0,
                tension: 0.2,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const idx = items[0]?.dataIndex ?? 0;
                    return `Distance: ${formatDistance(samples[idx]?.distance ?? 0)}`;
                  },
                  label: (item) => {
                    return `Elevation: ${(item.raw as number).toFixed(1)} m`;
                  },
                },
              },
            },
            scales: {
              x: {
                display: true,
                title: { display: true, text: 'Distance' },
                ticks: { maxTicksLimit: 6 },
              },
              y: {
                display: true,
                title: { display: true, text: 'Elevation (m)' },
              },
            },
          },
        });

        setChartReady(true);
      } catch (err) {
        console.warn('[ElevationProfileChart] Chart.js import failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (chartRef.current) {
        (chartRef.current as any).destroy?.();
        chartRef.current = null;
      }
    };
  }, [samples]);

  if (samples.length === 0) {
    return null;
  }

  return (
    <div
      data-testid={testId ?? 'elevation-profile-chart'}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}
    >
      <div style={{ height, position: 'relative' }}>
        <canvas ref={canvasRef} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: tokens.space[1],
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMono.size,
        }}
      >
        <div>
          <span style={{ color: tokens.color.ink.secondary, fontSize: '0.75em' }}>
            MIN
          </span>
          <div>{stats.minElevation.toFixed(1)} m</div>
        </div>
        <div>
          <span style={{ color: tokens.color.ink.secondary, fontSize: '0.75em' }}>
            MAX
          </span>
          <div>{stats.maxElevation.toFixed(1)} m</div>
        </div>
        <div>
          <span style={{ color: tokens.color.ink.secondary, fontSize: '0.75em' }}>
            ASCENT
          </span>
          <div>{stats.totalAscent.toFixed(1)} m</div>
        </div>
        <div>
          <span style={{ color: tokens.color.ink.secondary, fontSize: '0.75em' }}>
            DESCENT
          </span>
          <div>{stats.totalDescent.toFixed(1)} m</div>
        </div>
      </div>
    </div>
  );
}
